import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Loader2, RefreshCw, Check, X, Lightbulb, ChevronDown, Award, Sparkles } from "lucide-react";

const DIFFS = [
  { id: "any", label: "All" },
  { id: "easy", label: "Easy" },
  { id: "medium", label: "Medium" },
  { id: "hard", label: "Hard" },
];

export default function MathPractice() {
  const [catalog, setCatalog] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [subtopicId, setSubtopicId] = useState(null);
  const [difficulty, setDifficulty] = useState("any");
  const [problem, setProblem] = useState(null);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null); // "correct" | "wrong" | null
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [streak, setStreak] = useState(0);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const inputRef = useRef(null);

  // Load catalog once on mount
  useEffect(() => {
    api.get("/math/catalog").then((r) => {
      setCatalog(r.data.categories);
      const firstCat = Object.keys(r.data.categories)[0];
      setCategoryId(firstCat);
      const firstSub = Object.keys(r.data.categories[firstCat].subtopics)[0];
      setSubtopicId(firstSub);
    });
  }, []);

  // When category changes, pick the first subtopic
  useEffect(() => {
    if (!catalog || !categoryId) return;
    const subs = Object.keys(catalog[categoryId].subtopics);
    if (subs.length && !subs.includes(subtopicId)) {
      setSubtopicId(subs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, catalog]);

  const fetchProblem = async () => {
    if (!categoryId || !subtopicId) return;
    setResult(null);
    setAnswer("");
    setLoading(true);
    try {
      const r = await api.post("/math/generate", {
        category: categoryId,
        subtopic: subtopicId,
        difficulty,
      });
      setProblem(r.data);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      setProblem(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiProblem = async () => {
    if (!categoryId || !subtopicId) return;
    setResult(null);
    setAnswer("");
    setAiLoading(true);
    try {
      const r = await api.post("/math/ai-generate", {
        category: categoryId,
        subtopic: subtopicId,
        difficulty,
      });
      setProblem(r.data);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      // Fall back to curated on transient failure — the global interceptor
      // already toasted the user; keep the current problem visible.
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (categoryId && subtopicId) fetchProblem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, subtopicId, difficulty]);

  const submit = (e) => {
    e?.preventDefault();
    if (!problem || result != null) return;
    if (answer === "" || answer === null || answer === undefined) return;
    const userVal = parseFloat(answer);
    const tolerance = problem.tol != null ? problem.tol : (Number.isInteger(problem.answer) ? 0.01 : 0.01);
    const correct = Number.isFinite(userVal) && Math.abs(userVal - problem.answer) <= tolerance;
    setResult(correct ? "correct" : "wrong");
    setScore((s) => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    setStreak(correct ? streak + 1 : 0);
  };

  if (!catalog) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center" data-testid="math-page">
        <Loader2 className="animate-spin inline" size={20} />
      </div>
    );
  }

  const currentCat = catalog[categoryId];
  const currentSub = currentCat?.subtopics?.[subtopicId];

  return (
    <div className="max-w-4xl mx-auto" data-testid="math-page">
      <h1 className="font-display text-4xl font-bold mb-2">Math Lab</h1>
      <p className="text-base mb-6" style={{ color: "var(--text-secondary)" }}>
        Sharpen the math skills you'll need on test day — with worked-out reasoning after every problem.
      </p>

      {/* Category tabs */}
      <div className="flex gap-2 mb-4 flex-wrap overflow-x-auto" data-testid="math-cat-tabs">
        {Object.entries(catalog).map(([cid, cat]) => (
          <button
            key={cid}
            onClick={() => setCategoryId(cid)}
            className="pm-badge whitespace-nowrap"
            style={{
              background: categoryId === cid ? "var(--primary)" : "var(--muted)",
              color: categoryId === cid ? "white" : "var(--text)",
              cursor: "pointer",
              padding: "8px 14px",
              fontSize: "13px",
            }}
            data-testid={`math-cat-${cid}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Subtopic chip picker */}
      <div className="pm-card p-3 mb-4" data-testid="math-subtopic-picker">
        <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
          {currentCat?.label} · subtopics
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(currentCat?.subtopics || {}).map(([sid, sub]) => (
            <button
              key={sid}
              onClick={() => setSubtopicId(sid)}
              className="pm-badge inline-flex items-center gap-1"
              style={{
                background: subtopicId === sid ? "var(--accent)" : "var(--muted)",
                color: subtopicId === sid ? "white" : "var(--text)",
                cursor: "pointer",
                padding: "6px 12px",
                fontSize: "12px",
              }}
              data-testid={`math-sub-${sid}`}
            >
              {sub.label}
              <span className="opacity-60" style={{ fontSize: "10px" }}>{sub.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Difficulty */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="math-difficulty-tabs">
        <span className="text-xs uppercase tracking-wider self-center" style={{ color: "var(--text-muted)" }}>Difficulty:</span>
        {DIFFS.map((d) => (
          <button
            key={d.id}
            onClick={() => setDifficulty(d.id)}
            className="pm-badge"
            style={{
              background: difficulty === d.id ? "var(--primary)" : "var(--muted)",
              color: difficulty === d.id ? "white" : "var(--text)",
              cursor: "pointer",
              padding: "5px 12px",
              fontSize: "12px",
            }}
            data-testid={`math-diff-${d.id}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Problem card */}
      <div className="pm-card p-8 paper-grid" data-testid="math-problem-card">
        {loading || !problem ? (
          <div className="py-8 text-center">
            <Loader2 size={20} className="animate-spin inline" style={{ color: "var(--text-muted)" }} />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs uppercase font-semibold tracking-widest" style={{ color: "var(--text-muted)" }}>
                {problem.subtopic_label || problem.op}
              </span>
              {problem.difficulty && (
                <span
                  className="pm-badge text-xs capitalize"
                  style={{
                    background: problem.difficulty === "hard" ? "#c95e5e22" : problem.difficulty === "medium" ? "#c98a3b22" : "#4f794222",
                    color: problem.difficulty === "hard" ? "#c95e5e" : problem.difficulty === "medium" ? "#c98a3b" : "#4f7942",
                  }}
                  data-testid="math-problem-difficulty"
                >
                  {problem.difficulty}
                </span>
              )}
              {problem.ai_generated && (
                <span
                  className="pm-badge text-xs inline-flex items-center gap-1"
                  style={{ background: "#3f7fbf22", color: "#3f7fbf" }}
                  data-testid="math-ai-badge"
                  title="This problem was freshly generated by Claude"
                >
                  <Sparkles size={10} /> AI
                </span>
              )}
            </div>

            <div className="font-mono text-2xl md:text-3xl font-semibold mb-6 leading-relaxed" data-testid="math-prompt">
              {problem.prompt}
            </div>

            <form onSubmit={submit} className="flex gap-3 flex-wrap">
              <input
                ref={inputRef}
                type="number"
                step="any"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={result != null}
                className="pm-input font-mono text-xl flex-1 min-w-[200px]"
                placeholder="Your answer"
                data-testid="math-answer-input"
              />
              {result == null ? (
                <button
                  type="submit"
                  disabled={answer === "" || answer === null}
                  className="pm-btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="math-submit-btn"
                >
                  <Check size={16} /> Submit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={fetchProblem}
                  className="pm-btn-primary inline-flex items-center gap-2"
                  data-testid="math-next-btn"
                >
                  <RefreshCw size={16} /> Next problem
                </button>
              )}
            </form>

            {/* AI fresh-problem button — always available */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={fetchAiProblem}
                disabled={aiLoading || loading}
                className="pm-badge inline-flex items-center gap-1 text-xs disabled:opacity-50"
                style={{ background: "#3f7fbf18", color: "#3f7fbf", border: "1.5px solid #3f7fbf55", cursor: "pointer", padding: "6px 12px" }}
                data-testid="math-ai-generate-btn"
                title="Ask Claude to generate a fresh problem in this subtopic"
              >
                {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {aiLoading ? "Generating…" : "Generate AI problem"}
              </button>
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                Fresh AI-authored problem in this subtopic — same explanation format.
              </span>
            </div>

            {/* Result + explanation panel */}
            {result != null && (
              <div className="mt-6 space-y-3" data-testid="math-result-panel">
                {result === "correct" ? (
                  <div
                    className="p-4 rounded-xl flex items-start gap-3"
                    style={{ background: "#4f794215", border: "1.5px solid #4f7942" }}
                    data-testid="math-correct-msg"
                  >
                    <Check size={18} style={{ color: "#4f7942", flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <div className="font-semibold" style={{ color: "#4f7942" }}>Correct — streak of {streak}!</div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="p-4 rounded-xl flex items-start gap-3"
                    style={{ background: "#c95e5e15", border: "1.5px solid #c95e5e" }}
                    data-testid="math-wrong-msg"
                  >
                    <X size={18} style={{ color: "#c95e5e", flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <div className="font-semibold" style={{ color: "#c95e5e" }}>Not quite.</div>
                      <div className="text-sm mt-0.5">
                        Correct answer: <span className="font-mono font-semibold">{problem.answer}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Always show explanation on both correct + wrong */}
                {problem.explanation && (
                  <div
                    className="p-4 rounded-xl flex items-start gap-3"
                    style={{ background: "var(--muted)", border: "1.5px solid var(--border)" }}
                    data-testid="math-explanation"
                  >
                    <Lightbulb size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-muted)" }}>
                        Why
                      </div>
                      <div className="text-sm leading-relaxed">{problem.explanation}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Score + streak */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="pm-card p-4" data-testid="math-score-card">
          <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Score</div>
          <div className="font-display text-3xl font-bold">
            {score.correct}<span className="text-lg" style={{ color: "var(--text-muted)" }}>/{score.total}</span>
          </div>
          {score.total > 0 && (
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              {Math.round((score.correct / score.total) * 100)}% accuracy
            </div>
          )}
        </div>
        <div className="pm-card p-4" data-testid="math-streak-card">
          <div className="text-xs uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
            <Award size={12} /> Streak
          </div>
          <div className="font-display text-3xl font-bold" style={{ color: streak >= 5 ? "#c98a3b" : "var(--text)" }}>
            {streak}
          </div>
        </div>
      </div>
    </div>
  );
}
